import React, { JSX } from 'react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import ErrorState from './ErrorState';
import OrgAvatar from './OrgAvatar';
import './item-list.scss';

import { ReactComponent as PrevIcon } from '../../icons/prev.svg';
import { ReactComponent as NextIcon } from '../../icons/next.svg';

export interface ItemListItem {
  id: string;
  icon?: JSX.Element; // SVG icon name or React component
  img?: string; // Image URL
  imagePlaceholder?: string; // Letter to show in placeholder bubble when no image
  title: string;
  description?: string;
  subDescription?: string; // Optional sub-description below title+description
  pill?: {
    text: string;
    variant?:
      | 'default'
      | 'green'
      | 'red'
      | 'blue'
      | 'yellow'
      | 'light-blue'
      | 'beige'
      | 'magic';
    onCancel?: () => void;
  };
  date?: string; // ISO date string or formatted date
  actions?: JSX.Element[]; // Optional action components
}

export interface ItemListProps {
  heading?: string;
  items: ItemListItem[];
  className?: string;
  emptyImage?: string;
  emptyHeader?: string;
  emptyText?: string;
  loading?: boolean;
  error?: boolean;
  showPagination?: boolean;
  nextVisible?: boolean;
  prevVisible?: boolean;
  onNext?: () => void;
  onPrev?: () => void;
  onItemClick?: (itemId: string) => void;
  banner?: {
    icon: JSX.Element;
    text: string;
  };
}

const ItemList: React.FC<ItemListProps> = ({
  heading,
  items,
  className,
  emptyImage,
  emptyHeader,
  emptyText,
  loading,
  error,
  showPagination,
  nextVisible,
  prevVisible,
  onNext,
  onPrev,
  onItemClick,
  banner,
}) => {
  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(parseISO(dateString), {
        addSuffix: true,
      });
    } catch {
      return dateString; // Return as-is if not a valid date
    }
  };

  const getPillClassName = (variant?: string) => {
    switch (variant) {
      case 'green':
        return 'item-pill item-pill-green';
      case 'red':
        return 'item-pill item-pill-red';
      case 'blue':
        return 'item-pill item-pill-blue';
      case 'yellow':
        return 'item-pill item-pill-yellow';
      case 'light-blue':
        return 'item-pill item-pill-light-blue';
      case 'beige':
        return 'item-pill item-pill-beige';
      case 'magic':
        return 'item-pill item-pill-magic';
      default:
        return 'item-pill item-pill-default';
    }
  };

  if (loading) {
    return (
      <div className='item-list'>
        {heading && (
          <div className='item-list-header'>
            <h3>{heading}</h3>
          </div>
        )}

        <div className='item-list-content'>
          <div className='item-list-items'>
            <div className='item-list-item'>
              <div className='item-content-group'>
                <div></div>
                <div className='loading-circle'></div>
                <div className='loading-text-line'></div>
              </div>
            </div>
            <div className='item-list-item'>
              <div className='item-content-group'>
                <div></div>
                <div className='loading-circle'></div>
                <div className='loading-text-line'></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`item-list ${className || ''}`}>
        {heading && (
          <div className='item-list-header'>
            <h3>{heading}</h3>
          </div>
        )}

        <div className='item-list-content'>
          <ErrorState
            title='Failed to load'
            subtitle='There was an error loading the data. Please try again.'
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`item-list ${className || ''}`}>
      {heading && (
        <div className='item-list-header'>
          <h3>{heading}</h3>
        </div>
      )}

      {banner && (
        <div className='item-list-banner'>
          <div className='banner-left'>
            <div className='banner-icon'>{banner.icon}</div>
            <div className='banner-content'>{banner.text}</div>
          </div>
        </div>
      )}

      <div className='item-list-content'>
        {items.length === 0 ? (
          <div className='item-list-empty'>
            {emptyImage && (
              <div className='empty-image'>
                <img src={emptyImage} alt='Empty state' />
              </div>
            )}
            {emptyHeader && <h4 className='empty-header'>{emptyHeader}</h4>}
            {emptyText && <p className='empty-text'>{emptyText}</p>}
          </div>
        ) : (
          <div className='item-list-items'>
            {items.map((item) => (
              <div
                key={item.id}
                className={`item-list-item ${onItemClick ? 'clickable' : ''}`}
                onClick={() => onItemClick?.(item.id)}
              >
                {/* Group 1: Icon/Image + Title + Description */}
                <div className='item-content-group'>
                  {item.icon && <div className='item-icon'>{item.icon}</div>}
                  {item.img || item.imagePlaceholder ? (
                    <OrgAvatar
                      imageUrl={item.img}
                      orgName={item.imagePlaceholder || item.title}
                    />
                  ) : !item.icon ? (
                    <div className='item-placeholder'>
                      <span></span>
                    </div>
                  ) : null}
                  <div className='item-text-content'>
                    <div className='text-header'>
                      <h4 className='item-title'>{item.title}</h4>
                      {item.description && (
                        <span className='item-description'>
                          {item.description}
                        </span>
                      )}
                    </div>
                    {item.subDescription && (
                      <p className='item-sub-description'>
                        {item.subDescription}
                      </p>
                    )}
                  </div>
                </div>

                {/* Group 2: Pill */}
                <div className='item-pill-group'>
                  {item.pill && (
                    <div className={getPillClassName(item.pill.variant)}>
                      {item.pill.text}
                      {item.pill.onCancel && (
                        <button
                          className='pill-cancel-button'
                          onClick={(e) => {
                            e.stopPropagation();
                            item.pill!.onCancel!();
                          }}
                          aria-label='Dismiss'
                        >
                          ×
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Group 3: Date */}
                <div className='item-date-group'>
                  {item.date && (
                    <span className='item-date'>{formatDate(item.date)}</span>
                  )}
                </div>

                {/* Group 4: Actions */}
                {item.actions && item.actions.length > 0 && (
                  <div
                    className='item-actions-group'
                    onClick={(e) => e.stopPropagation()}
                  >
                    {item.actions.map((action, index) => (
                      <div key={index} className='item-action'>
                        {action}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Pagination */}
      {showPagination && (
        <div className='item-pagination-buttons'>
          <button
            className={`pagination-btn prev ${!prevVisible ? 'inactive' : ''}`}
            onClick={onPrev}
            disabled={!prevVisible}
          >
            <PrevIcon /> Previous
          </button>
          <button
            className={`pagination-btn next ${!nextVisible ? 'inactive' : ''}`}
            onClick={onNext}
            disabled={!nextVisible}
          >
            Next <NextIcon />
          </button>
        </div>
      )}
    </div>
  );
};

export default ItemList;
